
















































































import { computed, defineComponent, onBeforeUnmount, PropType, ref, watch } from "@vue/composition-api";
import { VaultFileUsageSubjectTypeEnum } from "@scrile/api-provider/dist/api/VaultsProvider";
import useVaultView from "@/hooks/useVaultView";
import { isAttachmentFile } from "@/lib/fileHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { AttachmentFile, PostFileData } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInputFileWithPreview from "@/components/BaseInputFileWithPreview";
import ImagePreview from "@/components/ImagePreview";
import PostFilePreview from "@/components/PostCreate/components/PostFilePreview";
import VaultModal from "@/components/VaultModal";
import MakeTrailerModal from "./MakeTrailerModal.vue";

type TrailerRange = { startTime: number; finishTime: number };

export default defineComponent({
  name: "TrailerVideo",
  components: {
    PostFilePreview,
    BaseIconButton,
    ImagePreview,
    MakeTrailerModal,
    VaultModal,
    BaseInputFileWithPreview,
    BaseButton,
  },
  model: {
    prop: "generateTrailer",
    event: "input",
  },
  props: {
    files: { type: Array as PropType<PostFileData[]>, required: true },
    mainVideo: { type: Object as PropType<PostFileData | null>, default: null },
    selectedTrailerRange: { type: Object as PropType<TrailerRange | null>, default: null },
    value: { type: Boolean, default: false },
    generateTrailer: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { showPrivateVault: showVault, showVaultOrNative } = useVaultView();

    const loading = ref(false);
    const showMakeTrailerModal = ref(false);
    const trailerRange = ref<TrailerRange | null>(props.selectedTrailerRange ?? null);
    const blobPreviewSrc = ref<string | null>(null);

    const showGeneratedTrailerPreview = computed(
      () => props.generateTrailer && !!trailerRange.value && !props.files.length
    );
    const generatedPreviewSrc = computed(() => {
      if (!props.mainVideo?.url || !trailerRange.value) return "/img/content-no-preview.svg";
      if (props.mainVideo.url.startsWith("blob")) {
        return blobPreviewSrc.value ?? "/img/content-no-preview.svg";
      }
      return MediaFilesController.getVideoThumbnail(props.mainVideo.url, 480);
    });

    const revokeBlobPreview = () => {
      if (blobPreviewSrc.value?.startsWith("blob")) {
        URL.revokeObjectURL(blobPreviewSrc.value);
      }
      blobPreviewSrc.value = null;
    };

    const onTrailerRangeReset = () => {
      trailerRange.value = null;
      revokeBlobPreview();
      emit("input", false);
      emit("trailerRangeReset");
    };

    const postSubject = {
      subjectId: "0",
      subjectType: VaultFileUsageSubjectTypeEnum.USER_CONTENT_SETS,
    };

    const onAddFiles = (files: (AttachmentFile | File)[]) => {
      loading.value = true;
      const options = {
        publicAccess: true,
        cb: () => (loading.value = false),
      };
      if (files.every(isAttachmentFile)) {
        emit("addFilesFromVault", files, options);
      } else {
        emit("addFiles", files, options);
      }
    };

    const onNativeAddFileHandler = (files: FileList) => {
      onAddFiles(Array.from(files));
    };

    const onTrailerRangeApply = ({
      startTime,
      finishTime,
      previewSrc,
    }: {
      startTime: number;
      finishTime: number;
      previewSrc?: string;
    }) => {
      trailerRange.value = { startTime, finishTime };
      revokeBlobPreview();
      blobPreviewSrc.value = previewSrc ?? null;
      emit("trailerRangeChange", { startTime, finishTime });
      emit("input", true);
      showMakeTrailerModal.value = false;
    };

    const onRemove = () => {
      props.files[0] ? emit("fileRemove", props.files[0].id) : emit("trailerRangeReset");
    };

    watch(
      () => props.selectedTrailerRange,
      value => {
        trailerRange.value = value ?? null;
      }
    );

    watch(
      () => props.mainVideo,
      newVal => {
        if (!newVal) {
          onTrailerRangeReset();
        }
      }
    );

    onBeforeUnmount(revokeBlobPreview);

    return {
      loading,
      showMakeTrailerModal,
      trailerRange,
      showGeneratedTrailerPreview,
      generatedPreviewSrc,
      showVault,
      postSubject,
      showVaultOrNative,
      onNativeAddFileHandler,
      onAddFiles,
      onTrailerRangeApply,
      onRemove,
    };
  },
});
